// src/rig/skeletonPose.js
import * as THREE from "three";

/**
 * Find the first SkinnedMesh under a root (mannequin / garment clone)
 */
export function getSkinnedMeshes(root) {
  const out = [];
  root?.traverse?.((o) => {
    if (o?.isSkinnedMesh && o?.skeleton) out.push(o);
  });
  return out;
}

/**
 * Cache each bone’s rest quaternion once (idempotent).
 * Stored on bone.userData._restQuat
 */
export function cacheRestPose(root) {
  const skins = getSkinnedMeshes(root);
  if (!skins.length) return;

  for (const skin of skins) {
    const skel = skin.skeleton;
    if (!skel?.bones?.length) continue;

    for (const bone of skel.bones) {
      if (!bone.userData._restQuat) bone.userData._restQuat = bone.quaternion.clone();
      if (!bone.userData._restPos) bone.userData._restPos = bone.position.clone();
    }
  }
}

/**
 * Restore bones to cached rest quats (A-pose / bind-like pose as loaded).
 */
export function restoreRestPose(root) {
  const skins = getSkinnedMeshes(root);
  if (!skins.length) return;

  for (const skin of skins) {
    const skel = skin.skeleton;
    if (!skel?.bones?.length) continue;

    for (const bone of skel.bones) {
      const q = bone.userData._restQuat;
      if (q) bone.quaternion.copy(q);

      const p = bone.userData._restPos;
      if (p) bone.position.copy(p);

      bone.updateMatrixWorld(true);
    }

    skel.update();
    skin.updateMatrixWorld(true);
  }

  root.updateMatrixWorld(true);
}

/* =========================================================
   HANG-POSE FIXES (CODE-ONLY)
   ---------------------------------------------------------
   1) Elbow straightening (axis-agnostic, rigify-safe)
      - Some GLBs have a baked elbow bend or weird bone roll.
      - We straighten by geometry, not by assuming hinge axis.

   2) Hand tilt relax (optional)
      - In hang mode, softly blend hand back toward its rest quat,
        so wrists don’t “tip” weirdly.
========================================================= */

const STRAIGHTEN_FLEX_THRESHOLD = 0.08; // <= this = "hang / basically straight"
const HAND_RELAX_STRENGTH = 0.85;       // 0..1 (higher = more rest-like)
const ENABLE_HAND_RELAX = false;

// temp objects (no alloc in loop)
const _elbowPos = new THREE.Vector3();
const _shoulderPos = new THREE.Vector3();
const _wristPos = new THREE.Vector3();
const _vFore = new THREE.Vector3();
const _vTarget = new THREE.Vector3();

const _qWorldNeeded = new THREE.Quaternion();
const _qParentWorld = new THREE.Quaternion();
const _qLocalDelta = new THREE.Quaternion();
const _qTmp = new THREE.Quaternion();

function boneOrNull(skel, names) {
  for (const n of names) {
    const b = skel.getBoneByName(n);
    if (b) return b;
  }
  return null;
}

function measureElbowAngle(skin, upper, hingeForearm, wristBone) {
  hingeForearm.getWorldPosition(_elbowPos);
  upper.getWorldPosition(_shoulderPos);
  wristBone.getWorldPosition(_wristPos);

  _vFore.subVectors(_wristPos, _elbowPos).normalize();        // elbow -> wrist
  _vTarget.subVectors(_elbowPos, _shoulderPos).normalize();   // elbow -> away from shoulder

  return _vFore.angleTo(_vTarget);
}

/**
 * Cache best hinge bone per-root and side so we don't probe every frame.
 * root.userData._elbowHinge = { L: "DEF-forearmL", R: "DEF-forearmR" }
 */
function getCachedElbowHingeName(root, side) {
  return root?.userData?._elbowHinge?.[side] || null;
}

function setCachedElbowHingeName(root, side, boneName) {
  if (!root) return;
  if (!root.userData) root.userData = {};
  if (!root.userData._elbowHinge) root.userData._elbowHinge = {};
  root.userData._elbowHinge[side] = boneName;
}

/**
 * Straighten elbow in hang mode by finding (once) which candidate bone actually changes the elbow angle,
 * then applying the full corrective rotation on that hinge.
 */
function straightenElbowHang(root, skin, side, { requestedFlexRad = null, hangFactor = null } = {}) {
  const skel = skin?.skeleton;
  if (!skel) return;

  // Prefer hangFactor if provided (poseToRigify meta)
  if (hangFactor != null) {
    if (hangFactor < 0.95) return;
  } else {
    if (requestedFlexRad == null) return;
    if (Math.abs(requestedFlexRad) > STRAIGHTEN_FLEX_THRESHOLD) return;
  }

  const upper = boneOrNull(skel, [
    `DEF-upper_arm${side}`,
    `DEF-upper_arm${side}001`,
  ]);
  if (!upper) return;

  const foreA = skel.getBoneByName(`DEF-forearm${side}`);
  const foreB = skel.getBoneByName(`DEF-forearm${side}001`);
  const upA = skel.getBoneByName(`DEF-upper_arm${side}`);
  const upB = skel.getBoneByName(`DEF-upper_arm${side}001`);

  const hand = boneOrNull(skel, [
    `DEF-hand${side}`,
    `DEF-hand${side}001`,
  ]);

  function firstBoneChild(b) {
    for (const c of b?.children || []) if (c?.isBone) return c;
    return null;
  }

  const wristBone = hand || firstBoneChild(foreA) || firstBoneChild(foreB);
  if (!wristBone) return;

  const cachedName = getCachedElbowHingeName(root, side);
  let hinge = cachedName ? skel.getBoneByName(cachedName) : null;

  if (!hinge) {
    const candidates = [foreA, foreB, upA, upB].filter(Boolean);
    if (!candidates.length) return;

    skin.updateMatrixWorld(true);
    skel.update();

    const elbowJoint = foreA || foreB || upA || upB;
    if (!elbowJoint) return;

    const base = measureElbowAngle(skin, upper, elbowJoint, wristBone);
    if (!isFinite(base) || base < 0.02) return;

    elbowJoint.getWorldPosition(_elbowPos);
    upper.getWorldPosition(_shoulderPos);
    wristBone.getWorldPosition(_wristPos);

    _vFore.subVectors(_wristPos, _elbowPos).normalize();
    _vTarget.subVectors(_elbowPos, _shoulderPos).normalize();
    _qWorldNeeded.setFromUnitVectors(_vFore, _vTarget);

    const saved = new Map();
    for (const b of candidates) saved.set(b, b.quaternion.clone());

    let best = null;
    let bestAngle = base;

    const probeStep = 0.15;
    for (const b of candidates) {
      for (const [bone, q0] of saved) bone.quaternion.copy(q0);

      b.parent?.getWorldQuaternion(_qParentWorld);
      _qLocalDelta.copy(_qParentWorld).invert();
      _qLocalDelta.multiply(_qWorldNeeded);
      _qLocalDelta.multiply(_qParentWorld);

      _qTmp.copy(_qLocalDelta);
      _qTmp.slerp(new THREE.Quaternion(), 1.0 - probeStep);
      b.quaternion.multiply(_qTmp);

      skin.updateMatrixWorld(true);
      skel.update();

      const a = measureElbowAngle(skin, upper, elbowJoint, wristBone);
      if (isFinite(a) && a < bestAngle) {
        bestAngle = a;
        best = b;
      }
    }

    for (const [bone, q0] of saved) bone.quaternion.copy(q0);

    if (!best) return;

    hinge = best;
    setCachedElbowHingeName(root, side, hinge.name);
  }

  const elbowJoint = foreA || foreB || hinge;
  if (!elbowJoint) return;

  skin.updateMatrixWorld(true);
  skel.update();

  elbowJoint.getWorldPosition(_elbowPos);
  upper.getWorldPosition(_shoulderPos);
  wristBone.getWorldPosition(_wristPos);

  _vFore.subVectors(_wristPos, _elbowPos).normalize();
  _vTarget.subVectors(_elbowPos, _shoulderPos).normalize();
  _qWorldNeeded.setFromUnitVectors(_vFore, _vTarget);

  hinge.parent?.getWorldQuaternion(_qParentWorld);
  _qLocalDelta.copy(_qParentWorld).invert();
  _qLocalDelta.multiply(_qWorldNeeded);
  _qLocalDelta.multiply(_qParentWorld);

  hinge.quaternion.multiply(_qLocalDelta);

  skel.update();
  skin.updateMatrixWorld(true);

  const after = measureElbowAngle(skin, upper, elbowJoint, wristBone);
  console.log("[ELBOW_FIXED]", side, {
    hinge: hinge.name,
    after: Number(after.toFixed(3)),
    wrist: wristBone.name,
  });
}

/**
 * In hang mode, softly relax hand back toward rest (kills annoying wrist tilt).
 */
function relaxHandHang(root, skin, side, { requestedFlexRad = null, hangFactor = null } = {}) {
  if (!ENABLE_HAND_RELAX) return;

  const skel = skin?.skeleton;
  if (!skel) return;

  if (hangFactor != null) {
    if (hangFactor < 0.85) return;
  } else {
    if (requestedFlexRad == null) return;
    if (Math.abs(requestedFlexRad) > STRAIGHTEN_FLEX_THRESHOLD) return;
  }

  const hand = boneOrNull(skel, [
    `DEF-hand${side}`,
    `DEF-hand${side}001`,
  ]);
  if (!hand) return;

  const rest = hand.userData?._restQuat;
  if (!rest) return;

  hand.quaternion.slerp(rest, HAND_RELAX_STRENGTH);
}

/**
 * Apply a poseSpec (array of { name, rot:[x,y,z] }) onto a skeleton.
 *
 * IMPORTANT:
 * - If reset=true, restore the whole skeleton to rest first (prevents accumulation).
 * - Even if reset=false, we still start each addressed bone from its cached rest quat.
 */
export function applyPoseToSkeleton(root, poseSpec, { reset = true } = {}) {
  if (!root) return;

  const skins = getSkinnedMeshes(root);
  if (!skins.length) return;

  const skin = skins[0];
  if (!skin?.skeleton) return;

  cacheRestPose(root);

  if (reset) restoreRestPose(root);

  // IMPORTANT:
  // We still allow solver-only / seed-only poses to pass through.
  // So do not early-return just because bones is empty.
  const bones = Array.isArray(poseSpec?.bones) ? poseSpec.bones : [];

  let reqForearmFlexL = null;
  let reqForearmFlexR = null;

  const hangL = poseSpec?.meta?.arm?.L?.hangFactor ?? null;
  const hangR = poseSpec?.meta?.arm?.R?.hangFactor ?? null;

  let foundCount = 0;
  const missedBones = [];

  for (const b of bones) {
    const bone = skin.skeleton.getBoneByName(b.name);
    if (!bone) {
      missedBones.push(b.name);
      continue;
    }
    foundCount++;

    const q = bone.userData._restQuat;
    if (q) bone.quaternion.copy(q);

    const p0 = bone.userData._restPos;
    if (p0) bone.position.copy(p0);

    const rx = b.rot?.[0] ?? 0;
    const ry = b.rot?.[1] ?? 0;
    const rz = b.rot?.[2] ?? 0;

    const px = b.pos?.[0] ?? 0;
    const py = b.pos?.[1] ?? 0;
    const pz = b.pos?.[2] ?? 0;

    if (b.name === "DEF-forearmL" || b.name === "DEF-forearmL001") reqForearmFlexL = rx;
    if (b.name === "DEF-forearmR" || b.name === "DEF-forearmR001") reqForearmFlexR = rx;

    bone.rotation.x += rx;
    bone.rotation.y += ry;
    bone.rotation.z += rz;

    bone.position.x += px;
    bone.position.y += py;
    bone.position.z += pz;

    bone.updateMatrixWorld(true);
  }

  skin.updateMatrixWorld(true);
  skin.skeleton.update();

  // Solver-driven relaxed arm refinement
  straightenElbowHang(root, skin, "L", { requestedFlexRad: reqForearmFlexL, hangFactor: hangL });
  straightenElbowHang(root, skin, "R", { requestedFlexRad: reqForearmFlexR, hangFactor: hangR });

  // Optional wrist cleanup for hanging arms
  relaxHandHang(root, skin, "L", { requestedFlexRad: reqForearmFlexL, hangFactor: hangL });
  relaxHandHang(root, skin, "R", { requestedFlexRad: reqForearmFlexR, hangFactor: hangR });

  console.log("[POSE_APPLY_RESULT]", {
    requested: bones.length,
    found: foundCount,
    missedBones,
    hangL,
    hangR,
  });

  skin.skeleton.update();
  skin.updateMatrixWorld(true);
}